/* ─────────────────────────────────────────────────────────────────────────────
   PTL FREIGHT → PTL Merchant Setup — API calls.

   Backend: proship_mongo /api/ptl/setup/* (ROLE_PROSHIPADMIN + PTL_ACCESS). No merchantId = the standard.

     POST  merchants/{id}/turn-on-ptl                  → 200; sets isPTLMerchant only (audited)
     GET   merchants                                   → [{ id, name, companyName, rateCardCouriers[], customTatLanes, allocationCouriers[], ownZoneMapPincodes }]
     GET   zone-map/summary?merchantId                 → { pincodesMapped, notServiceable, zonesInUse, zonesDefined, odaPincodes, greenTaxPincodes,
                                                           specialZonePincodes, merchantsWithOwnMap, merchantsWithCustomTat, latest, zones[{zoneId,name,area,pincodes}] }
     GET   zone-map/list?merchantId&zoneId&state&city&oda&greenTax&specialZone&pincodes&page&size → { rows[], total, page, size }
     GET   zone-map/download?merchantId | zone-map/template → CSV
     POST  zone-map/upload?merchantId&mode(UPDATE|REPLACE)&confirm&acknowledge (file) → upload check
     POST  zone-map/switch-to-standard?merchantId     → upload check
     GET   zone-map/history?merchantId&page&size       → [{ action, fileName, mode, courier, counts, createdBy, createdAt }] (audit log)
     GET   tat/grid?merchantId | ?courier              → { zones[{id,name,area}], cells[{zone,pickupZoneId,dropZoneId,minTat,maxTat,standardMin,standardMax,
                                                           source S|P|C, baseSource S|P}], customLanes, courierLanes, standardLanes, basisCourier,
                                                           courier, usedBy[], latest }   (standardMin/Max = the base under the cell)
     GET   tat/couriers                                → [{ courier, accountCode, notLive, lanes, merchants, latest }]
     POST  tat/basis?merchantId&courier                → grid; puts the merchant on the standard (no courier) or a courier's TAT
     GET   tat/analytics                               → { standardLanes, totals{merchants,lanes,faster,slower,sameMax,avgMaxDelta,belowStandardMin}, merchants[], topLanes[] }
     GET   tat/download?merchantId|courier → CSV
     POST  tat/upload?merchantId|courier&confirm&acknowledge (file) | tat/switch-to-standard?merchantId
     GET   tat/history?merchantId|courier&page&size    (audit log)

   Upload check: { confirmed, fileName, rowsInFile, counts{added,changed,unchanged,removed,...}, warnings[], needsAcknowledge,
                   errors[{row,key,reason}], version }. Any error rejects the whole file.

     GET   pricing/coverage?merchantId                 → { standardLanes, cards[{courier,lanesPriced,missingLanes[],unknownLanes[],uploadedOn}], latest }
     GET   pricing/template                            → CSV (one row per standard lane, no TAT columns)
     POST  pricing/upload?merchantId&courier&confirm&acknowledge (file) → upload check; confirm replaces that courier's card

     GET   allocation?merchantId                       → { couriers[{courier,accountCode,rateCard,selected,notLive}], selected[], latest }
     POST  allocation?merchantId&acknowledge  [prozoIds] → same, plus version; couriers without a rate card need acknowledge

     GET   merchants/agreements                        → { merchantId: { signed, validity, daysToExpiry, expiringDays, startDate, expiryDate,
                                                           creditDays, approvalStatus } } (own call, after merchants)
     GET   merchants/{id}/agreement                    → { signed, documentName, documentUploadedAt, documentUploadedBy, documentUrl (temporary
                                                           signed link), startDate, expiryDate, validity
                                                           (ACTIVE|EXPIRING|EXPIRED|NOT_STARTED|NONE), daysToExpiry, expiringDays, remarks,
                                                           creditDays (0 = advance), approvalStatus (NOT_SUBMITTED|PENDING|
                                                           APPROVED|REJECTED), approvalNote, approvalBy, approvalAt, rejectionReason, version }
     PUT   merchants/{id}/agreement  { startDate, expiryDate, creditDays (0-365), remarks, version } → same; back to PENDING on change
     POST  merchants/{id}/agreement/document (file: pdf, png, jpg) → same; the PTL agreement's own document, back to PENDING
     POST  merchants/{id}/agreement/approve { version } | agreement/reject { version, reason } → same
   ──────────────────────────────────────────────────────────────────────────── */
import { apiConstants, get, post, put } from '../../common/constants'

const BASE = apiConstants.PTL_SETUP

/** Drops empty values so the backend sees "not set" instead of "". */
const clean = (params) => Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== ''))
const qs = (params) => {
  const s = new URLSearchParams(clean(params)).toString()
  return s ? `?${s}` : ''
}
const blob = { responseType: 'blob' }

/** 'zone-map' or 'tat'. */
export const KIND = { ZONE_MAP: 'zone-map', TAT: 'tat' }

export const fetchMerchants = () => get(`${BASE}/merchants`).then((r) => r.data)

export const fetchZoneSummary = (merchantId) => get(`${BASE}/zone-map/summary`, clean({ merchantId })).then((r) => r.data)
export const fetchZoneList = (params) => get(`${BASE}/zone-map/list`, clean(params)).then((r) => r.data)
export const downloadZoneTemplate = () => get(`${BASE}/zone-map/template`, {}, blob)

export const fetchTatGrid = (merchantId, courier) => get(`${BASE}/tat/grid`, clean({ merchantId, courier })).then((r) => r.data)
/** Live PTL courier accounts with their own TAT (lanes given, merchants on it). */
export const fetchTatCouriers = () => get(`${BASE}/tat/couriers`).then((r) => r.data)
/** Puts a merchant on the standard TAT (courier empty) or on one courier's TAT; returns its grid. */
export const setTatBasis = (merchantId, courier) => post(`${BASE}/tat/basis${qs({ merchantId, courier })}`).then((r) => r.data)
/* One courier's TAT: the TAT kind with courier instead of merchantId. */
export const uploadCourierTat = (courier, { file, confirm, acknowledge }) => upload(KIND.TAT, { file, courier, confirm, acknowledge })
export const fetchCourierTatHistory = (courier, page = 0, size = 20) => get(`${BASE}/tat/history`, clean({ courier, page, size })).then((r) => r.data)
/** Custom TAT of every live PTL merchant against the standard: per merchant, totals, most customised lanes. */
export const fetchTatAnalytics = () => get(`${BASE}/tat/analytics`).then((r) => r.data)

/** courier: TAT only, a courier's TAT instead of the standard / a merchant's. */
export const download = (kind, merchantId, courier) => get(`${BASE}/${kind}/download`, clean({ merchantId, courier }), blob)

export const upload = (kind, { file, merchantId, courier, mode, confirm, acknowledge }) => {
  const form = new FormData()
  form.append('file', file)
  return post(`${BASE}/${kind}/upload${qs({ merchantId, courier, mode, confirm, acknowledge })}`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data)
}

export const switchToStandard = (kind, merchantId) => post(`${BASE}/${kind}/switch-to-standard${qs({ merchantId })}`).then((r) => r.data)

/** Change history (audit log) of a zone map / TAT / rate card / allocation, newest first: who, when, file, counts. */
export const fetchHistory = (kind, merchantId, page = 0, size = 20) =>
  get(`${BASE}/${kind}/history`, clean({ merchantId, page, size })).then((r) => r.data)

export const fetchPricingCoverage = (merchantId) => get(`${BASE}/pricing/coverage`, { merchantId }).then((r) => r.data)
export const downloadPricingTemplate = () => get(`${BASE}/pricing/template`, {}, blob)

/** Step 1 rate card for one courier: confirm=false checks only; confirm=true replaces that courier's card. */
export const uploadRateCard = ({ file, merchantId, courier, confirm, acknowledge }) => {
  const form = new FormData()
  form.append('file', file)
  return post(`${BASE}/pricing/upload${qs({ merchantId, courier, confirm, acknowledge })}`, form,
    { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data)
}

export const fetchAllocation = (merchantId) => get(`${BASE}/allocation`, { merchantId }).then((r) => r.data)
export const saveAllocation = (merchantId, couriers, acknowledge) =>
  post(`${BASE}/allocation${qs({ merchantId, acknowledge })}`, couriers).then((r) => r.data)

/* Onboard a merchant into PTL: shared merchant APIs, the same calls the D2C sign-up and the merchant details page make. */

/** Every live merchant (not only PTL), to pick one to onboard. Not the cached search: a type switched on a minute ago must show. */
export const searchAllMerchants = () =>
  post(apiConstants.MERCHANT_SEARCH, {
    offset: 0, limit: 10000, andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
    includes: 'id,name,companyName,merchantId,isPTLMerchant,isD2CMerchant,status',
  }).then((r) => r.data?.data || r.data || [])

/** POST merchant/create with the PTL type on (D2C off). Same fields and checks as the D2C wizard sign-up. */
export const createPtlMerchant = (f) =>
  post(apiConstants.MERCHANT_CREATE, {
    name: f.brand || f.business_name, companyName: f.business_name, email: f.email, contact_no: f.contact,
    address: f.address, pincode: Number(f.pincode) || null, city: f.city, state: f.state, country: 'India',
    isPTLMerchant: true, isD2CMerchant: false,
  }).then((r) => r.data)

/**
 * POST merchants/{id}/turn-on-ptl: sets the PTL merchant type only (audited). Not merchant/update: that one rewrites
 * fields the request does not send (it reset isTestMerchant on a test merchant).
 */
export const turnOnPtl = (merchantId) => post(`${BASE}/merchants/${merchantId}/turn-on-ptl`).then((r) => r.data)

/*
 * Agreement: ptl_merchant_agreement (shared with PTL billing). The signed document is the PTL agreement's own file, not
 * the merchant's shared upload, so a merchant that is also D2C keeps its D2C agreement untouched.
 */

/** Agreements of every live PTL merchant: a call of its own, so a slow agreement store never holds up the merchant list. */
export const fetchAgreements = () => get(`${BASE}/merchants/agreements`).then((r) => r.data || {})

/**
 * After a merchant list is shown, puts each merchant's agreement on it: m.agreement is undefined while loading, null when
 * the agreements could not be read. setMerchants is the list's state setter.
 */
export const loadAgreementsInto = (setMerchants) =>
  fetchAgreements()
    .then((byId) => setMerchants((ms) => ms && ms.map((m) => ({ ...m, agreement: byId[m.id] || null }))))
    .catch(() => setMerchants((ms) => ms && ms.map((m) => ({ ...m, agreement: null }))))

export const fetchAgreement = (merchantId) => get(`${BASE}/merchants/${merchantId}/agreement`).then((r) => r.data)
/** Dates and credit days; a change puts an approved agreement back to awaiting approval. */
export const saveAgreement = (merchantId, body) => put(`${BASE}/merchants/${merchantId}/agreement`, body).then((r) => r.data)
export const approveAgreement = (merchantId, version) => post(`${BASE}/merchants/${merchantId}/agreement/approve`, { version }).then((r) => r.data)
export const rejectAgreement = (merchantId, version, reason) =>
  post(`${BASE}/merchants/${merchantId}/agreement/reject`, { version, reason }).then((r) => r.data)
/** The PTL agreement's signed document (pdf, png, jpg); returns the agreement. Needs the dates and terms saved first. */
export const uploadAgreementDocument = (merchantId, file) => {
  const form = new FormData()
  form.append('file', file)
  return post(`${BASE}/merchants/${merchantId}/agreement/document`, form, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data)
}

/** Names of the merchant's pickup locations (lower case, trimmed), for the form's duplicate-name check. */
export const fetchPickupNames = (merchantId) =>
  get(`${apiConstants.MERCHANT}/${merchantId}`).then((r) => (r.data?.pickup_details || []).map((p) => (p.name || '').toLowerCase().trim()))

/** POST merchant/register_pickup/{id}: the save Pickup Locations makes for a new B2B pickup location. */
export const savePickupLocation = (merchantId, data) =>
  post(`${apiConstants.PICKUP_LOCATION}/${merchantId}`, { status: 'ACTIVE', address_type: 'PICKUP', country: 'India', ...data, shipmentType: 'B2B' })
    .then((r) => r.data)

/** Server message for a failed call (validation texts are written for ops, so show them as is). */
export const errorText = (e, fallback = 'Something went wrong') => e?.response?.data?.message || e?.message || fallback
